/**
 * <back-in-stock> — the Notify Me drawer's form (SAVETTE-70, snippets/back-in-stock-drawer.liquid).
 *
 * Subscribes the email to Klaviyo's back-in-stock notification for the selected color's variant,
 * through Klaviyo's client API with the store's public key (the site ID, safe in the browser):
 *
 *   POST https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=<public key>
 *   revision: 2026-10-15
 *
 * Request shape and revision verified on 22 Oct 2026 against
 * https://developers.klaviyo.com/en/reference/create_client_back_in_stock_subscription (revision
 * 2026-10-15, GA in Klaviyo's changelog; the old site used 2023-12-15). Success is 202 Accepted with
 * no body. The variant is Klaviyo's catalog id for a Shopify variant: $shopify:::$default:::<id>.
 *
 * Expects:
 *   - data-company-id (the public key) and data-variant-id (the variant the page rendered with)
 *   - a form [data-back-in-stock-form] with an email input, a [data-back-in-stock-submit] button
 *     and a [data-back-in-stock-message] line carrying the success, error and invalid strings
 *   - optionally [data-back-in-stock-variant], the selected color's name
 *
 * The selected variant follows the product-form:variant event assets/product-form.js sends on
 * every color change. On success the message replaces the button and the drawer closes after two
 * seconds, as the old site's did; on failure the drawer stays open with the error and the button
 * comes back.
 */
const KLAVIYO_REVISION = '2026-10-15';
const ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions';
const CLOSE_AFTER_MS = 2000;
// deliberately loose: one @, something either side, a dot in the domain; Klaviyo has the final say
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class BackInStock extends HTMLElement {
  connectedCallback() {
    this.companyId = this.dataset.companyId;
    this.variantId = this.dataset.variantId;
    this.form = this.querySelector('[data-back-in-stock-form]');
    this.input = this.form?.querySelector('input[type="email"]');
    this.submit = this.querySelector('[data-back-in-stock-submit]');
    this.message = this.querySelector('[data-back-in-stock-message]');
    this.variantEl = this.querySelector('[data-back-in-stock-variant]');
    this.drawer = this.closest('site-drawer');

    this.form?.addEventListener('submit', this.onSubmit);
    this.input?.addEventListener('input', this.onInput);
    document.addEventListener('product-form:variant', this.onVariant);
    this.drawer?.addEventListener('drawer:close', this.onClose);
  }

  disconnectedCallback() {
    document.removeEventListener('product-form:variant', this.onVariant);
    clearTimeout(this.closeTimer);
  }

  onVariant = (event) => {
    const variant = event.detail?.variant;
    if (!variant) return;
    this.variantId = String(variant.id);
    if (this.variantEl && variant.title) this.variantEl.textContent = variant.title;
  };

  onInput = () => {
    if (this.input?.getAttribute('aria-invalid') === 'true') {
      this.input.removeAttribute('aria-invalid');
      this.setMessage('');
    }
  };

  /** Back to a fresh form whenever the drawer closes, so a second color starts clean. */
  onClose = () => {
    clearTimeout(this.closeTimer);
    this.attempt = (this.attempt ?? 0) + 1;
    this.form?.reset();
    this.input?.removeAttribute('aria-invalid');
    if (this.submit) {
      this.submit.hidden = false;
      this.submit.disabled = false;
      this.submit.removeAttribute('aria-busy');
    }
    this.setMessage('');
  };

  setMessage(text) {
    if (this.message) this.message.textContent = text;
  }

  onSubmit = async (event) => {
    event.preventDefault();
    if (!this.input || !this.submit || this.submit.disabled) return;

    const email = this.input.value.trim();
    if (!EMAIL_PATTERN.test(email)) {
      this.input.setAttribute('aria-invalid', 'true');
      this.setMessage(this.message?.dataset.messageInvalid ?? '');
      this.input.focus();
      return;
    }

    // read focus before disabling the button: some browsers move focus to the body when a focused
    // button is disabled, and the success or error state must hand it back inside the drawer
    const hadFocus = document.activeElement === this.submit;
    this.submit.disabled = true;
    this.submit.setAttribute('aria-busy', 'true');
    this.setMessage('');
    // a request still in flight when the drawer closes (onClose resets the form) is ignored
    const attempt = (this.attempt = (this.attempt ?? 0) + 1);

    try {
      const response = await fetch(`${ENDPOINT}?company_id=${encodeURIComponent(this.companyId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          Accept: 'application/vnd.api+json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(this.payload(email)),
      });
      if (attempt !== this.attempt) return;
      if (!response.ok) throw new Error(`Klaviyo back in stock: ${response.status}`);

      // the button goes; if it held focus, hand focus to the message so it stays in the drawer
      this.submit.hidden = true;
      if (hadFocus) this.message?.focus();
      this.setMessage(this.message?.dataset.messageSuccess ?? '');
      this.closeTimer = setTimeout(() => this.drawer?.close(), CLOSE_AFTER_MS);
    } catch (error) {
      if (attempt !== this.attempt) return;
      console.error(error);
      this.setMessage(this.message?.dataset.messageError ?? '');
      this.submit.disabled = false;
      if (hadFocus) this.submit.focus();
    } finally {
      this.submit.removeAttribute('aria-busy');
    }
  };

  payload(email) {
    return {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${this.variantId}`,
            },
          },
        },
      },
    };
  }
}

if (!customElements.get('back-in-stock')) customElements.define('back-in-stock', BackInStock);
